import React, { memo, useState, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { AppNode } from '@/types';
import type { Editor } from '@tiptap/react';
import { X } from 'lucide-react';
import { FloatingToolbar } from '../FloatingToolbar';
import { RichTextInput } from '../RichTextInput';

export const SelfWriteFullscreenView: React.FC<{
  data: AppNode;
  editor: Editor | null;
  onClose: () => void;
}> = memo(({ data, editor, onClose }) => {
  const [copied, setCopied] = useState(false);
  const index = data.data.textNodeIndex || 1;

  // ESC 关闭全屏
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const handleCopy = useCallback(() => {
    if (editor) navigator.clipboard.writeText(editor.getText());
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [editor]);

  return createPortal(
    <div className="fixed inset-0 z-[9999] bg-black overflow-hidden">
      <div className="flex h-full w-full flex-col">
      <div className="flex items-center justify-between px-5 py-3 border-b border-white/10 flex-shrink-0">
        <span className="text-sm text-white/80 font-medium">{data.title || '文本节点'}</span>
        <button
          onClick={onClose}
          className="w-8 h-8 flex items-center justify-center rounded-lg text-white/50 hover:text-white hover:bg-white/10 transition-all"
        >
          <X size={18} />
        </button>
      </div>
      <div className="flex-shrink-0 flex justify-center py-4">
        <div className="w-full max-w-3xl px-4">
          <FloatingToolbar editor={editor} copied={copied} onCopy={handleCopy} showFullscreen={false} />
        </div>
      </div>
      <div className="flex-1 flex flex-col items-center px-4 overflow-y-auto rich-text-scroll">
        <div className="w-full max-w-3xl" style={{ minHeight: '60%' }}>
          <RichTextInput editor={editor} className="text-2xl" />
        </div>
      </div>
      </div>
    </div>,
    document.body
  );
});
